import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { tone, type Marker as ChartMarker, type Series as ChartSeries } from "../charts";
import { api } from "./api";
import { SLOW } from "./queries";

/**
 * 분석 차트 데이터 (시안 4572e49 analytics-data.ts 의 analyticsModel 을 실제 API 로).
 * 원천: GET /c/:id/seo/daily (키워드 × 영역 × KST 날짜의 마지막 수집), GET /c/:id/seo/posts (대상 고객사로 게시된 글).
 * 규칙 (인수인계 계약 2~5):
 * - daily 행이 없는 날 = 미수집(null). 수집됐는데 found=false = 상위 밖(101). 0 으로 채우지 않는다.
 * - 키워드 수는 customerId+keywordId 로 중복 제거. 지역(local)은 상위 5 전용이라 상위 100/10 집계에서 뺀다.
 * - 이전 기간 비교를 위해 조회일수의 2배(최대 180일)를 받아 현재/이전 구간으로 나눈다.
 */

export type DailyRow = {
  customerId: string;
  keywordId: string;
  keyword: string;
  area: string;
  day: string;
  collectedAt: string;
  bestRank: number | null;
  found: boolean;
  ourCount: number;
  total: number;
};
export type PostRow = { customerId: string; postedAt: string; title: string; url: string | null; keywords: string[] };

export const AREA_LABEL: Record<string, string> = { blog: "블로그", cafe: "카페", news: "뉴스", web: "웹문서", local: "지역", shop: "쇼핑" };
export const AREA_ORDER = ["blog", "cafe", "news", "web", "local", "shop"];
export const areaName = (a: string) => AREA_LABEL[a] ?? a;
export const topN = (area: string) => (area === "local" ? 5 : 100);
export const bandNames = ["1–3위", "4–10위", "11–30위", "31–100위", "상위 밖"];
const BAND_OPACITY = [1, 0.72, 0.48, 0.28];
export const rankValue = (v: number | null) => (v == null ? "미수집" : v > 100 ? "상위 밖" : `${v}위`);
export const RANK_NOTICE = "순위는 네이버 검색 API 결과 상위 100개(지역 5개) 안에서 우리 자산과 일치한 항목의 가장 높은 순위";

/** KST 날짜 키 (YYYY-MM-DD). */
export function kstDay(iso: string): string {
  return new Date(new Date(iso).getTime() + 9 * 3600_000).toISOString().slice(0, 10);
}
/** 오늘(KST)로 끝나는 days 개의 날짜. back 만큼 더 과거로 민다 (이전 기간). */
export function dayKeys(days: number, back = 0): string[] {
  const now = Date.now() + 9 * 3600_000;
  return Array.from({ length: days }, (_, i) => new Date(now - (days - 1 - i + back) * 86_400_000).toISOString().slice(0, 10));
}
export const kstTime = (iso: string) => new Date(new Date(iso).getTime() + 9 * 3600_000).toISOString().slice(11, 16);

/** 고객사들의 daily·posts 를 한 번에 받는다 (전체 고객사 합산이면 고객사마다 호출). */
export function useSeoAnalyticsData(customerIds: string[], days: number) {
  const fetchDays = Math.min(days * 2, 180);
  const daily = useQueries({
    queries: customerIds.map((id) => ({
      queryKey: ["seo", id, "daily", fetchDays],
      queryFn: () => api.page<Omit<DailyRow, "customerId">>(`/c/${id}/seo/daily?days=${fetchDays}`),
      staleTime: 60_000,
      refetchInterval: SLOW,
    })),
  });
  const posts = useQueries({
    queries: customerIds.map((id) => ({
      queryKey: ["seo", id, "posts", days],
      queryFn: () => api.page<Omit<PostRow, "customerId">>(`/c/${id}/seo/posts?days=${days}`),
      staleTime: 60_000,
      refetchInterval: SLOW,
    })),
  });
  const dailyRows = useMemo(() => customerIds.flatMap((id, i) => (daily[i]?.data?.items ?? []).map((r) => ({ ...r, customerId: id }))), [customerIds, daily]);
  const postRows = useMemo(() => customerIds.flatMap((id, i) => (posts[i]?.data?.items ?? []).map((p) => ({ ...p, customerId: id }))), [customerIds, posts]);
  return {
    daily: dailyRows,
    posts: postRows,
    loading: daily.some((q) => q.isLoading) || posts.some((q) => q.isLoading),
    error: daily.find((q) => q.error)?.error ?? posts.find((q) => q.error)?.error ?? null,
    refetch: () => [...daily, ...posts].forEach((q) => void q.refetch()),
  };
}

const kwKey = (r: { customerId: string; keywordId: string }) => `${r.customerId}|${r.keywordId}`;
export const rowKey = (r: { customerId: string; keywordId: string; area: string }) => `${r.customerId}|${r.keywordId}|${r.area}`;

export function analyticsModel(daily: DailyRow[], posts: PostRow[], days: number) {
  const labels = dayKeys(days);
  const previousLabels = dayKeys(days, days);
  const byDay = new Map<string, DailyRow[]>();
  for (const r of daily) byDay.set(r.day, [...(byDay.get(r.day) ?? []), r]);
  const at = (day: string) => byDay.get(day) ?? [];
  /** 하루의 키워드별 최고 순위 (지역 제외, 여러 영역은 가장 높은 순위). 수집이 없으면 null. */
  const keywordsOn = (day: string) => {
    const rows = at(day).filter((r) => r.area !== "local");
    if (!rows.length) return null;
    const best = new Map<string, number | null>();
    for (const r of rows) {
      const k = kwKey(r);
      const v = r.found ? r.bestRank : null;
      const prev = best.get(k);
      best.set(k, prev == null ? v : v == null ? prev : Math.min(prev, v));
    }
    return [...best.values()];
  };
  const metricFns = [
    (d: string) => new Set(at(d).map(kwKey)).size || null,
    (d: string) => keywordsOn(d)?.filter((v) => v != null).length ?? null,
    (d: string) => keywordsOn(d)?.filter((v) => v != null && v <= 10).length ?? null,
    (d: string) => {
      const ks = keywordsOn(d);
      return ks && ks.length ? Math.round((ks.filter((v) => v != null).length / ks.length) * 100) : null;
    },
  ];
  const names = ["추적 키워드", "상위 100 노출", "상위 10 키워드", "노출률"];
  const metrics: (ChartSeries & { previous: (number | null)[] })[] = names.map((name, i) => ({ name, values: labels.map(metricFns[i]!), previous: previousLabels.map(metricFns[i]!) }));
  const band = (rank: number | null) => (rank == null ? 4 : rank <= 3 ? 0 : rank <= 10 ? 1 : rank <= 30 ? 2 : 3);
  // 순위 구간은 순서가 있는 값 → 같은 강조색의 진하기로 (상위일수록 진함), 상위 밖은 회색
  const bands: ChartSeries[] = bandNames.map((name, i) => ({ name, values: labels.map((d) => keywordsOn(d)?.filter((v) => band(v) === i).length ?? null), color: i === 4 ? tone.muted : tone.main, opacity: i === 4 ? 1 : BAND_OPACITY[i] }));

  const markers: ChartMarker[] = posts
    .map((p) => ({ index: labels.indexOf(kstDay(p.postedAt)), label: `${kstDay(p.postedAt).slice(5).replace("-", ".")} ${kstTime(p.postedAt)} · ${p.title}` }))
    .filter((m) => m.index >= 0);
  const markersFor = (customerId: string, keyword: string) =>
    posts
      .filter((p) => p.customerId === customerId && p.keywords.includes(keyword))
      .map((p) => ({ index: labels.indexOf(kstDay(p.postedAt)), label: `${kstDay(p.postedAt).slice(5).replace("-", ".")} ${kstTime(p.postedAt)} · ${p.title}` }))
      .filter((m) => m.index >= 0);

  const byRow = new Map<string, Map<string, DailyRow>>();
  for (const r of daily) {
    const k = rowKey(r);
    if (!byRow.has(k)) byRow.set(k, new Map());
    byRow.get(k)!.set(r.day, r);
  }
  /** 행(고객사·키워드·영역)의 날짜별 순위. 상위 밖 = 101, 미수집 = null. */
  const history = (key: string, keys = labels) => keys.map((d) => {
    const r = byRow.get(key)?.get(d);
    return r ? (r.found && r.bestRank != null ? r.bestRank : 101) : null;
  });
  /** 선 차트용: 상위 밖도 선을 잇지 않는다. */
  const historyRaw = (key: string, keys = labels) => history(key, keys).map((v) => (v === 101 ? null : v));
  const field = (key: string, f: (r: DailyRow) => number) => labels.map((d) => {
    const r = byRow.get(key)?.get(d);
    return r ? f(r) : null;
  });
  const latestOf = (key: string) => {
    const m = byRow.get(key);
    if (!m) return undefined;
    const d = labels.filter((x) => m.has(x)).pop();
    return d ? m.get(d) : undefined;
  };

  // 오늘의 변동: 기간 안 마지막 수집일과 그 직전 수집일
  const collectedDays = labels.filter((d) => at(d).length);
  const lastDay = collectedDays.at(-1) ?? null;
  const prevDay = collectedDays.at(-2) ?? null;
  const current = lastDay ? at(lastDay) : [];
  const previous = prevDay ? at(prevDay) : [];
  const prevBy = new Map(previous.map((r) => [rowKey(r), r]));
  const moves = current.map((a) => {
    const b = prevBy.get(rowKey(a));
    const ra = a.found ? a.bestRank : null;
    const rb = b?.found ? b.bestRank : null;
    return {
      row: a,
      delta: ra != null && rb != null ? rb - ra : null,
      isNew: ra != null && b != null && rb == null,
      left: ra == null && rb != null,
    };
  });
  const lastCollectedAt = daily.reduce<string | null>((m, r) => (m == null || r.collectedAt > m ? r.collectedAt : m), null);
  return { labels, previousLabels, daily, posts, metrics, bands, markers, markersFor, history, historyRaw, field, latestOf, current, moves, lastDay, lastCollectedAt, at };
}
export type AnalyticsModel = ReturnType<typeof analyticsModel>;

/** 마지막으로 수집된 날의 값과 그 위치 (오늘 수집 전이면 어제 값). 기간 끝 칸이 비었다고 "—" 로 보이지 않게 */
export function lastValue(values: (number | null)[]): { value: number | null; index: number } {
  for (let i = values.length - 1; i >= 0; i--) if (values[i] != null) return { value: values[i]!, index: i };
  return { value: null, index: -1 };
}

export function metricDelta(series: { values: (number | null)[]; previous?: (number | null)[] }, unit = "개") {
  const { value: a, index } = lastValue(series.values);
  const b = index >= 0 ? series.previous?.[index] : null;
  return a == null || b == null ? "이전 기간 데이터 없음" : `${a - b > 0 ? "+" : ""}${a - b}${unit} · 이전 기간 같은 날 대비`;
}
